import React, { useState } from 'react';
import { TrendingUp, BarChart3, FileText } from 'lucide-react';
import { StoredReport, TrendDataPoint } from '../../utils/indexedDB';
import { TrendChart } from './TrendChart';
import { ReportList } from './ReportList';

interface HistoryPanelProps {
  reports: StoredReport[];
  trendData: TrendDataPoint[];
  onLoadReport: (report: StoredReport) => void;
  onDeleteReport: (id: string) => void;
}

type MetricView = 'revenue' | 'alerts' | 'services';

export function HistoryPanel({
  reports,
  trendData,
  onLoadReport,
  onDeleteReport,
}: HistoryPanelProps) {
  const [activeMetric, setActiveMetric] = useState<MetricView>('revenue');

  const metrics: { key: MetricView; label: string; icon: React.ReactNode }[] = [
    { key: 'revenue', label: 'Revenue', icon: <TrendingUp className="w-4 h-4" /> },
    { key: 'alerts', label: 'Alerts', icon: <BarChart3 className="w-4 h-4" /> },
    { key: 'services', label: 'Services', icon: <FileText className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Metric Selector */}
      <div className="flex gap-2">
        {metrics.map((metric) => (
          <button
            key={metric.key}
            onClick={() => setActiveMetric(metric.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeMetric === metric.key
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'bg-slate-800/50 text-slate-400 hover:text-slate-300 border border-transparent'
            }`}
          >
            {metric.icon}
            {metric.label}
          </button>
        ))}
      </div>

      {/* Trend Chart */}
      <TrendChart data={trendData} metric={activeMetric} />

      {/* Summary Stats */}
      {trendData.length > 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SummaryCard
            title="Total Reports"
            value={reports.length.toString()}
            subtitle="saved in history"
          />
          <SummaryCard
            title="Total Revenue"
            value={`$${trendData.reduce((sum, d) => sum + d.revenue, 0).toLocaleString()}`}
            subtitle="across all reports"
          />
          <SummaryCard
            title="Avg Alerts/Report"
            value={Math.round(
              trendData.reduce((sum, d) => sum + d.alerts, 0) / trendData.length
            ).toString()}
            subtitle="per upload"
          />
        </div>
      )}

      {/* Report List */}
      <ReportList
        reports={reports}
        onLoadReport={onLoadReport}
        onDeleteReport={onDeleteReport}
      />
    </div>
  );
}

function SummaryCard({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string;
  subtitle: string;
}) {
  return (
    <div className="card p-4">
      <p className="text-xs text-slate-400 uppercase tracking-wider">{title}</p>
      <p className="text-2xl font-bold text-slate-200 mt-1">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
    </div>
  );
}
